import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';

const StrategyPresetModal = ({ show, onClose }) => {
  const positionRef = useRef({ x: 0, y: 0 });
  const [position, setPosition] = useState(() => {
    if (typeof window === 'undefined') {
      return { x: 120, y: 120 };
    }

    const savedPosition = window.localStorage.getItem('strategyPresetModalPosition');
    if (savedPosition) {
      try {
        const parsed = JSON.parse(savedPosition);
        positionRef.current = parsed;
        return parsed;
      } catch (error) {
        console.error('Failed to parse saved strategy modal position', error);
      }
    }

    const fallback = {
      x: Math.max(window.innerWidth - 390, 20),
      y: 140
    };
    positionRef.current = fallback;
    return fallback;
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  useEffect(() => {
    if (!show || !isDragging) {
      return;
    }

    const handleMouseMove = (event) => {
      const newX = event.clientX - dragStart.x;
      const newY = event.clientY - dragStart.y;
      const updatedPosition = { x: newX, y: newY };
      positionRef.current = updatedPosition;
      setPosition(updatedPosition);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(
          'strategyPresetModalPosition',
          JSON.stringify(positionRef.current)
        );
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart, show]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return () => {};
    }

    return () => {
      window.localStorage.setItem(
        'strategyPresetModalPosition',
        JSON.stringify(positionRef.current)
      );
    };
  }, []);

  useEffect(() => {
    if (!show) {
      setIsDragging(false);
    }
  }, [show]);

  if (!show) {
    return null;
  }

  const handleMouseDown = (event) => {
    if (
      event.target.closest('.strategy-modal-close') ||
      event.target.closest('.strategy-preset-button')
    ) {
      return;
    }

    setIsDragging(true);
    setDragStart({
      x: event.clientX - positionRef.current.x,
      y: event.clientY - positionRef.current.y
    });
  };

  const strategyPlans = [
    {
      title: '100% Trailing Stop',
      description: 'For Risky Moves',
      extra: 'Stopout? → Set Alert → Buy back',
      backgroundColor: 'rgba(255, 193, 7, 0.15)',
      borderColor: 'rgba(255, 193, 7, 0.4)'
    },
    {
      title: '50% Fixed Stop, 50% Trailing Stop',
      description: 'For Best of Two Worlds',
      backgroundColor: 'rgba(13, 110, 253, 0.15)',
      borderColor: 'rgba(13, 110, 253, 0.35)'
    },
    {
      title: '100% Fixed Stop',
      description: 'For Sound Base Structures',
      extra: 'Profits? → Lift Fixed Stop',
      backgroundColor: 'rgba(25, 135, 84, 0.18)',
      borderColor: 'rgba(25, 135, 84, 0.4)'
    }
  ];

  return (
    <div
      className="strategy-floating-modal"
      style={{
        position: 'fixed',
        top: `${position.y}px`,
        left: `${position.x}px`,
        // Keep below Bootstrap modals (1055) so dialogs like Balance History stay on top
        zIndex: 1045,
        padding: '18px',
        backgroundColor: 'var(--card-bg, #ffffff)',
        borderRadius: '14px',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.25)',
        border: '1px solid var(--border-color, rgba(148, 163, 184, 0.4))',
        width: '392px',
        maxHeight: '85vh',
        overflowY: 'auto',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none'
      }}
      onMouseDown={handleMouseDown}
    >
      <div className="position-relative mb-3 text-center">
        <h5 className="mb-0">Strategy Plans</h5>
        <Button
          variant="link"
          size="sm"
          className="strategy-modal-close p-0 text-muted position-absolute top-0 end-0"
          onClick={onClose}
          aria-label="Close strategy plans"
        >
          <i className="bi bi-x-circle-fill fs-5"></i>
        </Button>
      </div>

      <div className="text-center" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
        {strategyPlans.map((plan) => (
          <div
            key={plan.title}
            className="mb-3 p-3 rounded"
            style={{
              backgroundColor: plan.backgroundColor || 'var(--bg-secondary)',
              border: `1px solid ${plan.borderColor || 'var(--border-color, rgba(148, 163, 184, 0.2))'}`
            }}
          >
            <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
              {plan.title}
            </div>
            <div className="text-muted">{plan.description}</div>
            {plan.extra && (
              <div className="text-muted fst-italic">{plan.extra}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default StrategyPresetModal;
